import { useState, useCallback, useEffect, ChangeEvent, useMemo } from 'react'
import { Search, Calendar, Wallet, AArrowDown, Users } from 'lucide-react'
import { EmptyContent } from '@/view/components/EmptyContent'
import { useDebounce } from '@/app/hooks/useDebounce'
import { useGetLeads } from '@/app/hooks/useGetLeads'
import { HandleLeadStatusParams } from '@/app/services/leadsService/handleLeadStatus'
import { useLeadMutations } from '@/app/hooks/useLeadMutations'
import { buildWhatsappUrl } from '@/app/utils/whatsapp'

export const useLeadsController = () => {
  const { updateLeadStatus } = useLeadMutations()
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearchValue = useDebounce(500, searchValue)
  const [contactedLeads, setContactedLeads] = useState<Set<string>>(new Set())
  const [sort, setSort] = useState<string>('createdAt')
  const [order, setOrder] = useState<1 | -1>(-1)
  const [page, setPage] = useState(1)
  const limit = 10

  const { data, isFetching, setParams, refetch } = useGetLeads({ limit })

  const setSearch = useCallback(() => {
    setParams((state) => ({
      ...state,
      search: debouncedSearchValue,
      skip: 0,
    }))
    setPage(1)
  }, [debouncedSearchValue, setParams])

  useEffect(() => {
    setSearch()
  }, [setSearch])

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  const openWhatsApp = ({ _id: id, phone }: { _id: string; phone: string }) => {
    const text = 'Olá, gostaria de simular uma nova antecipação do FGTS?'
    const url = buildWhatsappUrl(phone, text)

    handleLeadStatus({ id })
    window.open(url, '_blank')
  }

  const handleLeadStatus = async (input: HandleLeadStatusParams) => {
    try {
      await updateLeadStatus(input)
      setContactedLeads((prev) => new Set(prev).add(input.id))
      await refetch()
    } catch (error: any) {}
  }

  useEffect(() => {
    if (data) {
      const contactedFromData = new Set(
        data.leads.filter((c) => c.contacted).map((c) => c._id),
      )
      setContactedLeads(contactedFromData)
    }
  }, [data])

  const handleSortChange = (newSort: string) => {
    setSort((prevSort) => {
      if (prevSort === newSort) {
        setOrder((prevOrder) => (prevOrder === 1 ? -1 : 1))
        return newSort
      } else {
        setOrder(newSort === 'name' ? 1 : -1)
        return newSort
      }
    })
    setPage(1)
  }

  const sortIcon = useMemo(() => {
    switch (sort) {
      case 'createdAt':
        return <Calendar size={20} className="text-primaryHex" />
      case 'availableBalance':
        return <Wallet size={20} className="text-primaryHex" />
      case 'name':
        return <AArrowDown size={20} className="text-primaryHex" />
      default:
        return <Search size={20} className="text-primaryHex" />
    }
  }, [sort])

  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      sort,
      order: String(order),
      skip: (page - 1) * limit,
    }))
  }, [page, sort, order, setParams])

  const renderNoLeads = () => {
    const noLeads = data?.leads.length === 0
    return noLeads ? (
      <EmptyContent
        icon={<Users size={46} />}
        title="Ainda não há leads"
        description="Quando um cliente começar a autocontratação e parar no meio, ele surgirá aqui para que você entre em ação e feche a venda."
      />
    ) : null
  }

  const totalPages = Math.ceil((data?.pagination.total || 0) / limit)

  const handlePrevious = () => {
    if (page > 1) setPage(page - 1)
  }

  const handleNext = () => {
    if (page < totalPages) setPage(page + 1)
  }

  return {
    data,
    isFetching,
    searchValue,
    handleSearchInputChange,
    openWhatsApp,
    contactedLeads,
    sort,
    handleSortChange,
    sortIcon,
    renderNoLeads,
    page,
    totalPages,
    handlePrevious,
    handleNext,
  }
}
